'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useLazyQuery, useMutation } from '@apollo/client'
import { Popover, PopoverContent, PopoverMenuItem, PopoverTrigger } from '@canyon/ui/Popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@canyon/ui/Select'
import { Tag } from '@canyon/ui/Tag'
import { cn } from '@canyon/ui/utils'
import { fetchUserLocation } from '@canyon/utils/location'
import { RiCloseLine, RiErrorWarningLine, RiLoader4Line, RiMapPinLine } from '@remixicon/react'
import debounce from 'debounce'

import {
  LocationSearchInput_LocationsTypeaheadDocument,
  LocationSearchInput_UserStoreLocationDocument,
} from '@gql/graphql'

export interface ILocationSearchInputProps {
  className?: string
  maxLocations?: number
  onRadiusChange?: (radius: string) => void
  placeholder?: string
  radiusValue?: string
  setValue?: (locations: string[]) => void
  showRadius?: boolean
  value?: string[]
}

export const LocationSearchInput = ({
  className,
  maxLocations = 5,
  onRadiusChange = () => {},
  placeholder = 'Enter locations (U.S. only)',
  radiusValue = '0',
  setValue,
  showRadius = true,
  value = [],
}: ILocationSearchInputProps) => {
  const [inputValue, setInputValue] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [hasSearched, setHasSearched] = useState(false)
  const [showNoResults, setShowNoResults] = useState(false)
  const [hasError, setHasError] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const menuItemRefs = useRef<(HTMLLIElement | null)[]>([])
  const hasAttemptedLocationFetch = useRef(false)

  const [fetchLocations, { loading }] = useLazyQuery(
    LocationSearchInput_LocationsTypeaheadDocument,
    {
      onCompleted: (data) => {
        const results = data.locationsTypeahead || []
        // Filter out already selected locations
        const filteredResults = results.filter((location) => !value.includes(location))
        setSuggestions(filteredResults)
        setHasSearched(true)
        setShowNoResults(filteredResults.length === 0)
        setHasError(false)
        if (filteredResults.length > 0) {
          setPopoverOpen(true)
          setSelectedIndex(0)
        }
      },
      onError: (error) => {
        console.error('Location search error:', error)
        setSuggestions([])
        setHasSearched(true)
        setShowNoResults(false)
        setHasError(true)
        setPopoverOpen(false)
      },
    }
  )

  const debouncedFetchLocations = useMemo(
    () =>
      debounce((location: string) => {
        fetchLocations({ variables: { location } })
      }, 300),
    [fetchLocations]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setInputValue(newValue)
      setSelectedIndex(-1)
      setShowNoResults(false)
      setHasSearched(false)
      setHasError(false)

      if (newValue.trim() && value.length < maxLocations) {
        debouncedFetchLocations(newValue)
      } else {
        setSuggestions([])
        setPopoverOpen(false)
        setShowNoResults(false)
        setHasError(false)
      }
    },
    [debouncedFetchLocations, value.length, maxLocations]
  )

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion: string) => {
    if (value.includes(suggestion) || value.length >= maxLocations) {
      return
    }

    const newLocations = [...value, suggestion]
    setValue?.(newLocations)
    setInputValue('')
    setSuggestions([])
    setPopoverOpen(false)
    setShowNoResults(false)
    setHasSearched(false)
    setHasError(false)

    // Refocus input for adding more locations
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  // Handle location removal
  const handleLocationRemove = (locationToRemove: string) => {
    const newLocations = value.filter((location) => location !== locationToRemove)
    setValue?.(newLocations)
  }

  // Handle clear all locations
  const handleClearAll = () => {
    setValue?.([])
    setInputValue('')
    setSuggestions([])
    setPopoverOpen(false)
    setShowNoResults(false)
    setHasSearched(false)
    setHasError(false)
    inputRef.current?.focus()
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace to remove last location when input is empty
    if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      const newLocations = value.slice(0, -1)
      setValue?.(newLocations)
      return
    }

    if (!popoverOpen || suggestions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex])
        }
        break
      case 'Escape':
        setPopoverOpen(false)
        setSelectedIndex(-1)
        break
    }
  }

  useEffect(() => {
    if (selectedIndex >= 0 && menuItemRefs.current[selectedIndex]) {
      menuItemRefs.current[selectedIndex]?.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
      })
    }
  }, [selectedIndex])

  // Auto-scroll to show input when locations change
  useEffect(() => {
    if (scrollContainerRef.current) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth
        }
      }, 0)
    }
  }, [value])

  const [storeUserLocation] = useMutation(LocationSearchInput_UserStoreLocationDocument)

  useEffect(() => {
    const prepopulateUserLocation = async () => {
      if (value.length === 0 && !hasAttemptedLocationFetch.current) {
        hasAttemptedLocationFetch.current = true
        const location = await fetchUserLocation()
        if (location) {
          setValue?.([location])
          storeUserLocation({
            variables: { city: location.split(',')[0], state: location.split(',')[1] },
          })
        }
      }
    }

    prepopulateUserLocation()
  }, [setValue, value.length])

  // Determine what to show on the right
  const renderEndSlot = () => {
    return (
      <div className="flex items-center gap-2">
        {/* Status/action icons */}
        <div className="flex items-center">
          {loading && <RiLoader4Line className="text-muted-foreground animate-spin" size={14} />}

          {(showNoResults || hasError) && inputValue.trim() && (
            <span title={hasError ? 'Search failed' : 'No results found'}>
              <RiErrorWarningLine className="text-destructive" size={14} />
            </span>
          )}

          {value.length > 0 && !loading && !(showNoResults || hasError) && (
            <button
              className="flex items-center justify-center hover:text-foreground transition-colors"
              onClick={handleClearAll}
              title="Clear all locations"
              type="button"
            >
              <RiCloseLine className="text-muted-foreground" size={14} />
            </button>
          )}
        </div>

        {showRadius && (
          <div className="border-l border-border pl-4">
            <Select onValueChange={onRadiusChange} value={radiusValue}>
              <SelectTrigger className="bg-transparent border-0 h-auto p-0 shadow-none focus:ring-0 gap-1 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">0 mi</SelectItem>
                <SelectItem value="5">5 mi</SelectItem>
                <SelectItem value="25">25 mi</SelectItem>
                <SelectItem value="50">50 mi</SelectItem>
                <SelectItem value="100">100 mi</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    )
  }

  const isMaxReached = value.length >= maxLocations

  return (
    <Popover onOpenChange={setPopoverOpen} open={popoverOpen}>
      <PopoverTrigger asChild>
        <div className={cn('relative w-full', className)} onClick={(e) => e.preventDefault()}>
          {/* Input wrapper with tags as start slot */}
          <div className="relative flex w-full items-center justify-center">
            <div className="relative flex w-full items-center">
              <div
                className={cn(
                  'flex h-8 w-full max-w-full px-4 py-2 text-sm rounded-full bg-background-secondary border-0.5',
                  `placeholder:text-muted-foreground/50 focus-within:outline-none disabled:cursor-not-allowed
                  disabled:opacity-50`,
                  (showNoResults || hasError) && inputValue.trim() && hasSearched
                    ? 'border-destructive focus-within:border-destructive focus-within:ring-destructive'
                    : 'border-border focus-within:ring-ring',
                  'focus-within:ring-1 focus-within:ring-offset-0',
                  isMaxReached && 'opacity-50 cursor-not-allowed',
                  'items-center gap-2',
                  'lg:min-w-[350px] lg:w-fit'
                )}
              >
                {/* Start slot - Map icon */}
                <div className="flex-shrink-0">
                  <RiMapPinLine className="text-muted-foreground" size={14} />
                </div>

                {/* Middle section with tags and input */}
                <div
                  className="flex-1 flex items-center gap-1 flex-nowrap min-w-0"
                  ref={scrollContainerRef}
                >
                  {/* Selected locations tags */}
                  {value.map((location) => (
                    <Tag
                      className="group pr-1 flex-shrink-0"
                      key={location}
                      size="sm"
                      variant="blue"
                    >
                      <span className="whitespace-nowrap">{location}</span>
                      <button
                        className="ml-1 hover:text-white/80 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleLocationRemove(location)
                        }}
                        type="button"
                      >
                        <RiCloseLine size={12} />
                      </button>
                    </Tag>
                  ))}

                  {/* Input field */}
                  <input
                    className={cn(
                      'bg-transparent outline-none text-sm px-2 border-0 flex-1',
                      'placeholder:text-muted-foreground/50',
                      isMaxReached && 'cursor-not-allowed'
                    )}
                    disabled={isMaxReached}
                    onChange={handleInputChange}
                    onFocus={() => {
                      // Ensure input is visible when focused
                      if (scrollContainerRef.current) {
                        scrollContainerRef.current.scrollLeft =
                          scrollContainerRef.current.scrollWidth
                      }
                      if (suggestions.length > 0 && !isMaxReached) {
                        setPopoverOpen(true)
                        setSelectedIndex(0)
                      }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      value.length === 0
                        ? placeholder
                        : isMaxReached
                          ? `Max ${maxLocations}`
                          : 'Add more...'
                    }
                    ref={inputRef}
                    value={inputValue}
                  />
                </div>

                {/* End slot - Right icons */}
                <div className="flex-shrink-0">{renderEndSlot()}</div>
              </div>
            </div>
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0 max-h-60 overflow-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
        sideOffset={8}
      >
        {suggestions.map((suggestion, index) => (
          <PopoverMenuItem
            className={cn(
              'flex items-center gap-2 px-3 py-2',
              index === selectedIndex && 'bg-accent'
            )}
            key={suggestion}
            onClick={() => handleSuggestionClick(suggestion)}
            onMouseEnter={() => setSelectedIndex(index)}
            ref={(el) => {
              if (el) {
                menuItemRefs.current[index] = el
              }
            }}
          >
            <RiMapPinLine className="text-muted-foreground flex-shrink-0" size={14} />
            <span className="text-sm">{suggestion}</span>
          </PopoverMenuItem>
        ))}
      </PopoverContent>
    </Popover>
  )
}
